import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { formatCurrencyCents } from '../mocks/wipAiFixture'

const Section = styled.section`
  margin-top: 28px;
`

const Card = styled.div`
  background: ${p => p.theme.colors.semantic.surface};
  border: 1px solid ${p => p.theme.colors.semantic.border};
  border-radius: 12px;
  box-shadow: ${p => p.theme.colors.semantic.shadowSm};
  padding: 0;
  overflow: hidden;
`

const TblHead = styled.div`
  display: flex;
  align-items: center;
  padding: 14px 18px;
  border-bottom: 1px solid ${p => p.theme.colors.semantic.border};
  gap: 12px;
`

const HeadT = styled.span`
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`

const HeadSub = styled.span`
  font-size: 12px;
  color: ${p => p.theme.colors.semantic.textMuted};
  margin-left: auto;
`

const ExpRow = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 2fr 1fr;
  gap: 18px;
  align-items: center;
  padding: 12px 18px;
  border-bottom: 1px solid ${p => p.theme.colors.semantic.border};
  &:last-child {
    border-bottom: none;
  }
`

const Acct = styled.div`
  font-weight: 500;
`

const Amt = styled.div`
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-variant-numeric: tabular-nums;
  text-align: right;
  font-weight: 600;
`

const WipAiExpensesBreakdown = ({ expensesDetails, sparks, iso }) => {
  const groups = {}
  for (const e of expensesDetails) {
    if (!groups[e.account]) groups[e.account] = { account: e.account, total: 0, items: [] }
    groups[e.account].total += e.cost
    groups[e.account].items.push(e)
  }
  const rows = Object.values(groups).sort((a, b) => b.total - a.total)
  const anomalies = { Travel: true, 'Research Services': true }

  return (
    <Section id="expenses">
      <Card>
        <TblHead>
          <HeadT>Expenses Breakdown</HeadT>
          <HeadSub>By account · 12-week trend</HeadSub>
        </TblHead>
        {rows.map((r, i) => {
          const spark = sparks[r.account] || []
          const mx = Math.max(...spark, 1)
          const pts = spark
            .map((v, j) => `${(j / (spark.length - 1)) * 100},${28 - (v / mx) * 24 - 2}`)
            .join(' ')
          const isAnom = anomalies[r.account]
          return (
            <ExpRow key={i}>
              <Acct>{r.account}</Acct>
              <Amt>{formatCurrencyCents(r.total, iso)}</Amt>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg
                  viewBox="0 0 100 28"
                  preserveAspectRatio="none"
                  style={{ width: 120, height: 22 }}
                >
                  <polyline
                    points={pts}
                    fill="none"
                    stroke={isAnom ? '#D98E00' : '#4241BF'}
                    strokeWidth="1.5"
                  />
                  {isAnom && (
                    <circle
                      cx="100"
                      cy={28 - (spark[spark.length - 1] / mx) * 24 - 2}
                      r="2.5"
                      fill="#D98E00"
                    />
                  )}
                </svg>
                {isAnom ? (
                  <span style={{ fontSize: 11, color: '#D98E00', fontWeight: 600 }}>
                    ↑ above trend
                  </span>
                ) : (
                  <span style={{ fontSize: 12, color: '#676767' }}>stable</span>
                )}
              </div>
              <Amt style={{ color: '#676767', fontSize: 12 }}>
                {r.items.length} item{r.items.length === 1 ? '' : 's'}
              </Amt>
            </ExpRow>
          )
        })}
      </Card>
    </Section>
  )
}

WipAiExpensesBreakdown.propTypes = {
  expensesDetails: PropTypes.arrayOf(PropTypes.object).isRequired,
  sparks: PropTypes.object.isRequired,
  iso: PropTypes.string.isRequired,
}

export default WipAiExpensesBreakdown
